import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Mail, MessageSquare, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_EMAIL_TEMPLATES = [
  {
    id: 'welcome',
    name: 'Welcome Email',
    channel: 'email',
    subject: 'Welcome to Its-Sold.com - Your FSBO Journey Starts Here!',
    body: `Hello {{lead_name}},

Welcome to Its-Sold.com! We're excited to help you with your property at {{property_address}}.

As a For Sale By Owner platform, we're here to support you every step of the way with:
- Free listing exposure
- Professional tools and resources
- Access to vetted DFW realtors (if you need help)
- AI-powered pricing insights

Your next steps:
1. Complete your property listing
2. Upload high-quality photos
3. Review our seller resources

Need help? Reply to this email or call us at (214) 771-9840.

Best regards,
The Its-Sold.com Team`
  },
  {
    id: 'followup_1',
    name: 'Follow-up Day 3',
    channel: 'email',
    subject: 'How is your listing coming along?',
    body: `Hi {{lead_name}},

Just checking in on your property at {{property_address}}. 

Have you had a chance to:
✓ Upload photos?
✓ Complete the description?
✓ Review pricing recommendations?

**Quick Tip:** Listings with 5+ photos get 3x more views!

Need assistance? We're here to help.

Best,
Its-Sold.com Team`
  },
  {
    id: 'pricing_alert',
    name: 'Pricing Strategy',
    channel: 'email',
    subject: 'Smart Pricing Tips for {{property_address}}',
    body: `Hi {{lead_name}},

Pricing is critical for FSBO success. Here's what we've learned:

**For your property at {{property_address}}:**
- Current DFW market: Median ~$323K
- Inventory up 10-15% (competitive market)
- Our AI suggests: $\{{suggested_price}}

**Tips:**
1. Price competitively from the start
2. Monitor views and inquiries
3. Be ready to adjust if needed

View your AI pricing analysis in your dashboard.

Need a realtor consultation? We can connect you with local experts.

Sincerely,
Its-Sold.com`
  }
];

const DEFAULT_SMS_TEMPLATES = [
  {
    id: 'sms_welcome',
    name: 'Welcome SMS',
    channel: 'sms',
    body: `Hi {{lead_name}}! Welcome to Its-Sold.com. Your FSBO listing for {{property_address}} is being reviewed. Need help? Reply or visit our website. Reply STOP to opt out.`
  },
  {
    id: 'sms_reminder',
    name: 'Action Reminder',
    channel: 'sms',
    body: `Hi {{lead_name}}, don't forget to complete your listing for {{property_address}}! Add photos for 3x more views. Login: its-sold.com Reply STOP to opt out.`
  }
];

const DripCampaignTemplates = () => {
  const [templates, setTemplates] = useState([...DEFAULT_EMAIL_TEMPLATES, ...DEFAULT_SMS_TEMPLATES]);
  const [editDialog, setEditDialog] = useState({ open: false, template: null, isNew: false });
  const [previewDialog, setPreviewDialog] = useState({ open: false, template: null });
  const [formData, setFormData] = useState({
    name: '',
    channel: 'email',
    subject: '',
    body: ''
  });

  const openNewTemplate = (channel = 'email') => {
    setFormData({
      name: '',
      channel: channel,
      subject: '',
      body: ''
    });
    setEditDialog({ open: true, template: null, isNew: true });
  };

  const openEditTemplate = (template) => {
    setFormData({
      name: template.name,
      channel: template.channel,
      subject: template.subject || '',
      body: template.body
    });
    setEditDialog({ open: true, template: template, isNew: false });
  };

  const saveTemplate = () => {
    if (!formData.name || !formData.body) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.channel === 'email' && !formData.subject) {
      toast.error('Email templates require a subject line');
      return;
    }

    const newTemplate = {
      id: editDialog.isNew ? `custom-${Date.now()}` : editDialog.template.id,
      name: formData.name,
      channel: formData.channel,
      subject: formData.subject,
      body: formData.body,
      custom: true
    };

    if (editDialog.isNew) {
      setTemplates([...templates, newTemplate]);
      toast.success('Template created successfully');
    } else {
      setTemplates(templates.map(t => t.id === editDialog.template.id ? newTemplate : t));
      toast.success('Template updated successfully');
    }

    setEditDialog({ open: false, template: null, isNew: false });
  };

  const deleteTemplate = (templateId) => {
    setTemplates(templates.filter(t => t.id !== templateId));
    toast.success('Template deleted');
  };

  const renderPreview = (template) => {
    const sampleData = {
      lead_name: 'John Smith',
      property_address: '123 Main St, Dallas, TX 75001',
      suggested_price: '425,000'
    };

    let preview = template.body;
    Object.keys(sampleData).forEach(key => {
      preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), sampleData[key]);
    });

    return preview;
  };

  const emailTemplates = templates.filter(t => t.channel === 'email');
  const smsTemplates = templates.filter(t => t.channel === 'sms');

  return (
    <div className="space-y-6">
      {/* Email Templates */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Email Templates ({emailTemplates.length})</h3>
          </div>
          <Button
            size="sm"
            onClick={() => openNewTemplate('email')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            New Email Template
          </Button>
        </div>

        <div className="grid gap-4">
          {emailTemplates.map((template) => (
            <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold text-gray-900">{template.name}</h4>
                  <p className="text-sm text-gray-600">Subject: {template.subject}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPreviewDialog({ open: true, template })}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditTemplate(template)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {template.custom && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteTemplate(template.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 line-clamp-2">{template.body}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* SMS Templates */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold">SMS Templates ({smsTemplates.length})</h3>
          </div>
          <Button
            size="sm"
            onClick={() => openNewTemplate('sms')}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            New SMS Template
          </Button>
        </div>

        <div className="grid gap-4">
          {smsTemplates.map((template) => (
            <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900">{template.name}</h4>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPreviewDialog({ open: true, template })}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditTemplate(template)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {template.custom && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteTemplate(template.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 line-clamp-2">{template.body}</p>
              <p className="text-xs text-gray-400 mt-1">
                Length: {template.body.length} chars {template.body.length > 160 && '(⚠️ Multiple SMS)'}
              </p>
            </div>
          ))}
        </div>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => {
        if (!open) setEditDialog({ open: false, template: null, isNew: false });
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editDialog.isNew ? 'Create New Template' : 'Edit Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Welcome Email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Channel *</label>
              <Select
                value={formData.channel}
                onValueChange={(val) => setFormData({ ...formData, channel: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.channel === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line *</label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., Welcome to Its-Sold.com"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Body *
                <span className="text-xs text-gray-500 ml-2">
                  Available variables: {`{{lead_name}}, {{property_address}}, {{suggested_price}}`}
                </span>
              </label>
              <Textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={formData.channel === 'email' ? 12 : 4}
                placeholder={formData.channel === 'email' 
                  ? "Write your email message here...\n\nUse {{lead_name}} for personalization."
                  : "Write your SMS message... Keep it under 160 chars for single SMS."}
              />
              {formData.channel === 'sms' && (
                <p className={`text-xs mt-1 ${formData.body.length > 160 ? 'text-orange-600' : 'text-gray-500'}`}>
                  {formData.body.length} / 160 characters
                  {formData.body.length > 160 && ' (Will send as multiple SMS)'}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialog({ open: false, template: null, isNew: false })}
            >
              Cancel
            </Button>
            <Button onClick={saveTemplate} className="bg-orange-600 hover:bg-orange-700">
              {editDialog.isNew ? 'Create Template' : 'Update Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => {
        if (!open) setPreviewDialog({ open: false, template: null });
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
          </DialogHeader>
          {previewDialog.template && (
            <div className="space-y-4">
              {previewDialog.template.channel === 'email' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Subject:</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{previewDialog.template.subject}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Message Body:</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded whitespace-pre-wrap border border-gray-200">
                  {renderPreview(previewDialog.template)}
                </div>
              </div>
              <p className="text-xs text-gray-500 italic">
                * Preview uses sample data. Actual messages will use real lead information.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewDialog({ open: false, template: null })}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DripCampaignTemplates;
